// packages/console/src/components/UserManagementModal.tsx
//
// Owner/admin user management. The owner manages everyone; an admin manages only the
// accounts they created (the created_by hierarchy, enforced server-side in tenancy).
// Provisioning or resetting returns a one-time password shown here once; it is never
// retrievable again, so the panel highlights it and offers copy.
import { useCallback, useEffect, useState } from "react";
import { Icon } from "./Icons";
import {
  listMembers,
  provisionCollaborator,
  resetCollaboratorPassword,
  removeMember,
  type MemberView,
  type CollaboratorCredential,
} from "../api/tenancy";

interface Props {
  workspaceId: string;
  currentUserId: string;
  currentRole: string; // "owner" | "admin"
  onClose: () => void;
}

// canManage mirrors tenancy canManageTarget: owner manages anyone (but not self here);
// admin manages only member-role accounts they created. Server enforces regardless.
function canManage(role: string, uid: string, m: MemberView): boolean {
  if (m.userId === uid) return false;
  if (role === "owner") return true;
  if (role === "admin") return m.role === "member" && m.createdBy === uid;
  return false;
}

export function UserManagementModal({ workspaceId, currentUserId, currentRole, onClose }: Props) {
  const [members, setMembers] = useState<MemberView[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  // The one-time credential to display (from provision or reset), shown once.
  const [cred, setCred] = useState<CollaboratorCredential | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    listMembers(workspaceId)
      .then((r) => setMembers(r.members))
      .catch(() => setErr("Could not load users."))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(refresh, [refresh]);

  const doAdd = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      setErr("Enter a valid email.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const c = await provisionCollaborator(workspaceId, e, name.trim() || e);
      setCred(c);
      setEmail("");
      setName("");
      refresh();
    } catch (x) {
      setErr(messageFor(x, "Could not add the user."));
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (m: MemberView) => {
    setBusy(true);
    setErr(null);
    try {
      setCred(await resetCollaboratorPassword(workspaceId, m.userId));
    } catch (x) {
      setErr(messageFor(x, "Could not reset the password."));
    } finally {
      setBusy(false);
    }
  };

  const doRemove = async (m: MemberView) => {
    if (!window.confirm(`Remove ${m.name || m.email}? They lose access immediately.`)) return;
    setBusy(true);
    setErr(null);
    try {
      await removeMember(workspaceId, m.userId);
      refresh();
    } catch (x) {
      setErr(messageFor(x, "Could not remove the user."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "rgba(0,0,0,0.55)",
        display: "grid",
        placeItems: "center",
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 640,
          maxWidth: "92vw",
          maxHeight: "86vh",
          overflow: "auto",
          background: "var(--bg-panel)",
          border: "1px solid var(--line-strong)",
          borderRadius: "var(--r-md)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid var(--line)" }}>
          <span style={{ font: "var(--t-label)", color: "#fff" }}>Manage users</span>
          <button className="ae-b" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <Icon name="x" size={16} color="var(--fg3)" />
          </button>
        </div>

        {cred && <CredentialCard cred={cred} onDismiss={() => setCred(null)} />}

        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--line)" }}>
          <div style={{ font: "var(--t-micro)", color: "var(--fg3)", marginBottom: 8 }}>Add a user (creates a password account under you)</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@domain.com"
              style={inputStyle}
            />
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" style={inputStyle} />
            <button className="ae-b" onClick={doAdd} disabled={busy} style={primaryBtn(busy)}>
              Add
            </button>
          </div>
        </div>

        {err && <div style={{ padding: "8px 16px", color: "var(--err)", font: "var(--t-body-sm)" }}>{err}</div>}

        <div style={{ padding: "8px 8px 14px" }}>
          {loading && <div style={{ padding: 12, color: "var(--fg4)", font: "var(--t-body-sm)" }}>Loading...</div>}
          {!loading &&
            members.map((m) => {
              const manageable = canManage(currentRole, currentUserId, m);
              return (
                <div key={m.userId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 8px", borderBottom: "1px solid var(--line)" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: "var(--t-body-sm)", color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {m.name || m.email} {m.userId === currentUserId && <span style={{ color: "var(--fg4)" }}>(you)</span>}
                    </div>
                    <div style={{ font: "400 11px var(--font-mono)", color: "var(--fg3)" }}>{m.email}</div>
                  </div>
                  <span style={rolePill(m.role)}>{m.role}</span>
                  <span style={{ width: 120, font: "var(--t-micro)", color: "var(--fg4)", textAlign: "right" }}>
                    {m.createdByName ? `by ${m.createdByName}` : ""}
                  </span>
                  <div style={{ display: "flex", gap: 6, width: 150, justifyContent: "flex-end" }}>
                    {manageable ? (
                      <>
                        <button className="ae-b" onClick={() => doReset(m)} disabled={busy} style={ghostBtn} title="Reset password">
                          Reset
                        </button>
                        <button className="ae-b" onClick={() => doRemove(m)} disabled={busy} style={dangerBtn} title="Remove user">
                          Remove
                        </button>
                      </>
                    ) : (
                      <span style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>{m.userId === currentUserId ? "" : "managed by owner"}</span>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

function CredentialCard({ cred, onDismiss }: { cred: CollaboratorCredential; onDismiss: () => void }) {
  const [copied, setCopied] = useState<"" | "pw" | "msg">("");
  const loginUrl = window.location.origin;
  // A ready-to-send handoff message: the sign-in URL, email, and one-time password,
  // so the admin can paste it straight into an email/chat to the new user.
  const message =
    `You've been added to the aether-edit console.\n\n` +
    `Sign in at: ${loginUrl}\n` +
    `Email: ${cred.email}\n` +
    `Temporary password: ${cred.password}\n\n` +
    `Use "Sign in with password". This password is shown only once, so keep it safe; ` +
    `you can change it later.`;
  const flash = (which: "pw" | "msg", text: string) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(""), 1500);
    });
  };
  return (
    <div style={{ margin: "12px 16px", padding: 12, border: "1px solid var(--blue-500)", borderRadius: "var(--r-sm)", background: "rgba(40,90,200,0.12)" }}>
      <div style={{ font: "var(--t-micro)", color: "var(--fg2)", marginBottom: 8 }}>
        One-time credential for {cred.email}. There is no email sent, so copy the message and send it
        to them now. The password is shown only once.
      </div>
      <pre
        style={{
          margin: 0,
          marginBottom: 8,
          padding: 10,
          borderRadius: "var(--r-xs)",
          background: "rgba(0,0,0,0.25)",
          font: "400 11px var(--font-mono)",
          color: "#fff",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          userSelect: "all",
        }}
      >
        {message}
      </pre>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button className="ae-b" onClick={() => flash("msg", message)} style={primaryBtn(false)}>
          {copied === "msg" ? "Copied message" : "Copy message"}
        </button>
        <button className="ae-b" onClick={() => flash("pw", cred.password)} style={ghostBtn}>
          {copied === "pw" ? "Copied" : "Copy password only"}
        </button>
        <button className="ae-b" onClick={onDismiss} style={{ ...ghostBtn, marginLeft: "auto" }}>
          Done
        </button>
      </div>
    </div>
  );
}

function messageFor(x: unknown, fallback: string): string {
  const msg = x instanceof Error ? x.message : "";
  if (msg.includes("403") || msg.toLowerCase().includes("forbidden")) return "You can only manage accounts you created.";
  if (msg.includes("409") || msg.toLowerCase().includes("exists") || msg.toLowerCase().includes("taken")) return "A user with that email already exists.";
  return fallback;
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: "7px 9px",
  borderRadius: "var(--r-xs)",
  border: "1px solid var(--line)",
  background: "var(--bg-input, #11151c)",
  color: "#fff",
  font: "var(--t-body-sm)",
};

function primaryBtn(busy: boolean): React.CSSProperties {
  return {
    padding: "7px 14px",
    borderRadius: "var(--r-xs)",
    border: "none",
    background: busy ? "var(--fg4)" : "var(--blue-500)",
    color: "#fff",
    cursor: busy ? "not-allowed" : "pointer",
    font: "var(--t-label)",
  };
}

const ghostBtn: React.CSSProperties = {
  padding: "5px 10px",
  borderRadius: "var(--r-xs)",
  border: "1px solid var(--line)",
  background: "transparent",
  color: "var(--fg1)",
  cursor: "pointer",
  font: "var(--t-micro)",
};

const dangerBtn: React.CSSProperties = { ...ghostBtn, color: "var(--err)" };

function rolePill(role: string): React.CSSProperties {
  const tone = role === "owner" ? "var(--blue-400)" : role === "admin" ? "#4caf50" : "var(--fg3)";
  return { font: "var(--t-micro)", color: tone, width: 54, textTransform: "capitalize" };
}
